import * as React from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { SignedInUserFragment } from '../../__generated__/graphql';
import { useRouterState } from '@tanstack/react-router';
import { sanitizeUser } from '../../analytics';
import { createId } from '@paralleldrive/cuid2';
import mixpanel from 'mixpanel-browser';

const ENVIRONMENT = process.env.NODE_ENV;

type TrackPageViewProps = {
  url?: string;
  eventProperties?: any;
};

type AnalyticsContextType = {
  user: SignedInUserFragment | null;
  trackEvent: (eventType: string, eventValue: string, eventProperties?: any) => Promise<any>;
  trackPageView: (props?: TrackPageViewProps) => Promise<any>;
  identifyUser: (user: SignedInUserFragment, isUserDeleted?: boolean) => void;
};

const AnalyticsContext = createContext<AnalyticsContextType>({
  user: null,
  trackEvent: async () => {},
  trackPageView: async () => {},
  identifyUser: () => {},
});

const ANONYMOUS_ID_KEY = 'pi_anonymous_id';

const usePathname = () => {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  return pathname;
};

const useSearchParams = () => {
  const searchParams = useRouterState({
    select: (state) => new URLSearchParams(state.location.search),
  });
  return searchParams;
};

const getOrCreateAnonymousId = (): string => {
  if (typeof window === 'undefined') return createId();

  try {
    let storedId = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (!storedId) {
      storedId = createId();
      localStorage.setItem(ANONYMOUS_ID_KEY, storedId);
    }
    return storedId;
  } catch (error) {
    console.error('Failed to get/create anonymous ID:', error);
    return createId();
  }
};

export const AnalyticsProvider = ({
  children,
  user,
  mixpanelLoaded,
}: {
  children: React.ReactNode;
  user: SignedInUserFragment | null;
  mixpanelLoaded: boolean;
}) => {
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const initialPageViewTracked = useRef(false);
  const previousUrl = useRef('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setAnonymousId(getOrCreateAnonymousId());
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (
        typeof window === 'undefined' ||
        ENVIRONMENT === 'development' ||
        !mixpanelLoaded
      )
        return;
      try {
        const url = pathname;
        trackPageView({ url });
        previousUrl.current = url;
        initialPageViewTracked.current = true;
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (
        initialPageViewTracked.current ||
        typeof window === 'undefined' ||
        ENVIRONMENT === 'development' ||
        !mixpanelLoaded
      )
        return;
      try {
        const url = pathname;
        trackPageView({ url });
        previousUrl.current = url;
        initialPageViewTracked.current = true;
      } catch (error) {
        console.error(error);
      }
    })();
  }, [mixpanelLoaded]);

  useEffect(() => {
    if (initialPageViewTracked.current) {
      if (ENVIRONMENT === 'development') return;
      const currentUrl = pathname;
      if (currentUrl !== previousUrl.current) {
        trackPageView({ url: currentUrl });
        previousUrl.current = currentUrl;
      }
    }
  }, [pathname, searchParams]);

  const identifyUser = (user: SignedInUserFragment, isUserDeleted = false) => {
    mixpanel.identify(user.id);
    mixpanel.people.set(sanitizeUser(user, isUserDeleted));
  };

  const getContextProperties = () => ({
    url: window.location.href,
    path: pathname,
    referrer: document.referrer,
    search: searchParams.toString(),
    title: document.title,
    userAgent: navigator.userAgent,
    platform: 'web',
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    viewportHeight: window.innerHeight,
    viewportWidth: window.innerWidth,
    pixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  });

  const trackEvent = async (eventType: string, eventValue: string, eventProperties: any = {}) => {
    if (ENVIRONMENT === 'development' || typeof window === 'undefined') return;

    const contextProperties = getContextProperties();

    const payload = {
      eventType,
      eventValue,
      eventProperties,
      contextProperties,
      userId: user?.id || null,
      identifierId: anonymousId,
    };

    try {
      mixpanel.track(eventType, {
        $locale: navigator.language,
        $mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ),
        eventValue,
        ...eventProperties,
        contextProperties,
        ...(user?.id ? { userId: user.id } : { identifierId: anonymousId }),
      });

      await fetch('https://tracking-29769531815.us-east1.run.app/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-pi-tracking-token': process.env.PI_TRACKING_TOKEN!,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const trackPageView = async ({ url, eventProperties }: TrackPageViewProps) => {
    if (ENVIRONMENT === 'development' || typeof window === 'undefined') return;
    const eventValue = url || pathname;
    const contextProperties = getContextProperties();

    const payload = {
      eventType: 'page_view',
      eventValue,
      eventProperties,
      contextProperties,
      userId: user?.id || null,
      identifierId: anonymousId,
    };

    try {
      mixpanel.track('page_view', {
        $locale: navigator.language,
        $mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        ),
        eventValue,
        ...eventProperties,
        contextProperties,
        ...(user?.id ? { userId: user.id } : { identifierId: anonymousId }),
      });

      await fetch('https://tracking-29769531815.us-east1.run.app/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-pi-tracking-token': process.env.PI_TRACKING_TOKEN!,
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AnalyticsContext.Provider value={{ user, trackEvent, trackPageView, identifyUser }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => useContext(AnalyticsContext);
